import { renderFormattedDate } from 'utils';
import { FormattedMessage } from 'react-intl';
import AddIcon from '@mui/icons-material/Add';
import { PermissionHandler } from 'components';
import ListIcon from '@mui/icons-material/List';
import { User, ProjectReportComment } from 'library/types';
import { Box, IconButton, Tooltip, Typography, alpha, useTheme } from '@mui/material';

type ProjectMonitoringCommentsCellProps<RowType extends object = object> = {
  row: RowType;
  comments?: ProjectReportComment[];
  currentUserId: number | string;
  onAddComment: (row: RowType, anchorEl: HTMLElement) => void;
  onViewAll: (row: RowType, anchorEl: HTMLElement) => void;
  users: User[];
};

const ProjectMonitoringCommentsCell = <RowType extends object = object>({
  row,
  comments = [],
  currentUserId,
  onAddComment,
  onViewAll,
  users,
}: ProjectMonitoringCommentsCellProps<RowType>) => {
  const theme = useTheme();
  const sortedComments = [...comments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
  const lastComment = sortedComments.length > 0 ? sortedComments[sortedComments.length - 1] : null;

  return (
    <Box
      sx={{
        width: '100%',
        py: 1,
        pr: 8, // Space for icons
        position: 'relative',
        overflow: 'hidden',
        '&:hover .cell-actions, &:focus-within .cell-actions': {
          opacity: 1,
          pointerEvents: 'auto',
        },
      }}>
      {/* Table Cell Header Actions */}
      <Box
        className="cell-actions"
        sx={{
          position: 'absolute',
          top: 8,
          right: 0,
          opacity: 1,
          pointerEvents: 'auto',
          transition: 'opacity 0.2s ease',
          zIndex: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(4px)',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          '@media (hover: hover) and (pointer: fine)': {
            opacity: 0,
            pointerEvents: 'none',
          },
        }}>
        <PermissionHandler requiredPermissionCodes={['add-project-report-comment']}>
          <Tooltip title={<FormattedMessage id="projectStepManaging.addComment" defaultMessage="Yorum Ekle" />}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onAddComment(row, e.currentTarget);
              }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </PermissionHandler>

        <Tooltip title={<FormattedMessage id="projectReport.viewAllComments" defaultMessage="Yorumları Listele" />}>
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onViewAll(row, e.currentTarget);
            }}>
            <ListIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {!lastComment ? (
        <Typography variant="caption" color="text.disabled">
          <FormattedMessage id="projectStepManaging.noCommentYet" defaultMessage="Henüz yorum eklenmemiş" />
        </Typography>
      ) : (
        <Box sx={{ position: 'relative' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {renderFormattedDate(lastComment.createdAt, true)} •{' '}
            {users.find((u) => u.id === lastComment.userId)?.nameSurname || '...'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.35,
            }}>
            {lastComment.commentText}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProjectMonitoringCommentsCell;
